<script setup lang="ts">
import { type SizeConditional, useExtractSize } from '#imports';
interface IProps {
    type?: SizeConditional<'small' | 'medium' | 'large' | 'discount'>;
    price: number;
    percent?: number;
    prefix?: string;
    free?: boolean;
    sign?: '+' | '-';
}
const {
    type = 'medium',
    percent = 0,
    prefix = '',
    free,
    sign,
    price,
} = defineProps<IProps>();

const { mobile, desktop } = useExtractSize(() => type, 'medium');

const formattedPrice = computed(() =>
    formatMoney(price, {
        unitClassName: 'toman',
        includeUnit: type !== 'discount',
        free: free && type !== 'discount',
        sign,
    })
);
</script>
<template>
    <div class="mr-price-root" :class="[desktop, `mobile-${mobile}`]">
        <slot name="prefix">
            <span v-if="prefix" class="prefix">{{ prefix }}</span>
        </slot>
        <span class="price" v-html="formattedPrice" />
        <div v-if="type === 'discount'" class="discount">٪{{ percent }}</div>
    </div>
</template>

<style lang="scss" scoped>
@mixin small {
    .prefix {
        @include tg-s($w5, usePalette($p-cool-gray, $p-600));
    }
    .price {
        @include tg-xl($w7, usePalette($p-blue, $p-500));
        &:deep() {
            .toman {
                @include tg-m($w5, usePalette($p-cool-gray, $p-600));
                margin-right: 4px;
            }
        }
    }
}
@mixin medium {
    .prefix {
        @include tg-l($w5, usePalette($p-cool-gray, $p-600));
    }
    .price {
        @include tg-display4($w7, usePalette($p-blue, $p-500));
        &:deep() {
            .toman {
                @include tg-xl($w5, usePalette($p-cool-gray, $p-600));
                margin-right: 4px;
            }
        }
    }
}
@mixin large {
    .prefix {
        @include tg-xl($w5, usePalette($p-cool-gray, $p-600));
    }
    .price {
        @include tg-display3($w7, usePalette($p-blue, $p-500));
        &:deep() {
            .toman {
                @include tg-xl($w5, usePalette($p-cool-gray, $p-600));
                margin-right: 4px;
            }
        }
    }
}
// eslint-disable-next-line vue-scoped-css/require-selector-used-inside
.mr-price-root {
    display: flex;
    align-items: center;
    white-space: nowrap;
    @include f-gap(4px);
    &.small {
        @include small;
    }
    &.medium {
        @include medium;
    }
    &.large {
        @include large;
    }
    &.mobile-small {
        @include mobile-only {
            @include small;
        }
    }
    &.mobile-medium {
        @include mobile-only {
            @include medium;
        }
    }
    &.mobile-large {
        @include mobile-only {
            @include large;
        }
    }
    &.discount {
        .prefix {
            @include tg-xs($w5, usePalette($p-cool-gray, $p-600));
        }
        .price {
            @include tg-l($w5, usePalette($p-cool-gray, $p-600));
            text-decoration: line-through;
        }
        .discount {
            height: 20px;
            background-color: usePalette($p-green, $p-500);
            border-radius: 12px;
            padding: 4px;
            @include tg-m($w7, white);
            line-height: 16px;
        }
    }
}
</style>
